import { LOCAL_STORAGE_KEYS } from "#root/utils/localStorage";
import { useState } from "react";

// Utility functions to get data from local storage
export const getCurrentTenantFromStorage = () => 
    localStorage.getItem(LOCAL_STORAGE_KEYS.TENANT_ID);

export const getCurrentUserFromStorage = () => {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEYS.CURRENT_USER_KEY);
    if (!stored) return null;
    try {
        return JSON.parse(stored);
    } catch {
        return null;
    }
};

// Get current tenant from local storage
export const useCurrentTenant = () => {
    const [tenantId] = useState(() => getCurrentTenantFromStorage());
    return { data: tenantId };
};

// Get current user from local storage
export const useCurrentUser = () => {
    const [user] = useState(() => getCurrentUserFromStorage());
    return { data: user };
};

// Get/set selected incident in local storage
export const useVTrackerSelectedIncident = (initialIncidentId?: string | null) => {
    const [selectedIncidentId, setSelectedIncidentId] = useState(() => 
        localStorage.getItem(LOCAL_STORAGE_KEYS.VTRACKER_SELECTED_INCIDENT) ?? initialIncidentId ?? null
    );

    const setVTrackerSelectedIncidentId = (value: string | null) => {
        setSelectedIncidentId(value);
        if (value) {
            localStorage.setItem(LOCAL_STORAGE_KEYS.VTRACKER_SELECTED_INCIDENT, value);
        }
    };

    return { selectedIncidentId, setVTrackerSelectedIncidentId };
};

export const useVAdminSelectedIncident = (initialIncidentId?: string | null) => {
    const [selectedIncidentId, setSelectedIncidentId] = useState(() => 
        localStorage.getItem(LOCAL_STORAGE_KEYS.VADMIN_SELECTED_INCIDENT) ?? initialIncidentId ?? null
    );

    const setVAdminSelectedIncidentId = (value: string | null) => {
        setSelectedIncidentId(value);
        if (value) {
            localStorage.setItem(LOCAL_STORAGE_KEYS.VADMIN_SELECTED_INCIDENT, value);
        }
    };

    return { selectedIncidentId, setVAdminSelectedIncidentId };
};

// Get/set unitId in local storage
export const useLocalUnitId = (initialUnitId?: string | null) => {
    const [unitId, setUnitIdState] = useState(() => 
        localStorage.getItem(LOCAL_STORAGE_KEYS.UNIT_ID) ?? initialUnitId ?? null
    );

    const setUnitId = (value: string | null) => {
        setUnitIdState(value);
        if (value) {
            localStorage.setItem(LOCAL_STORAGE_KEYS.UNIT_ID, value);
        }
    };

    return { unitId, setUnitId };
};

// Get/set department in local storage
export const useLocalDepartment = (initialDepartment?: string | null) => {
    const [department, setDepartmentState] = useState(() => 
        localStorage.getItem(LOCAL_STORAGE_KEYS.DEPARTMENT) ?? initialDepartment ?? null
    );

    const setDepartment = (value: string | null) => {
        setDepartmentState(value);
        if (value) {
            localStorage.setItem(LOCAL_STORAGE_KEYS.DEPARTMENT, value);
        }
    };

    return { department, setDepartment };
};

// Get/set cookie consent in local storage
export const useCookieConsent = () => {
    const [cookieConsent, setCookieConsent] = useState<boolean | undefined>(() => {
        const stored = localStorage.getItem(LOCAL_STORAGE_KEYS.COOKIE_CONSENT);
        return stored === null ? undefined : stored === 'true';
    });

    const setCookieConsentValue = (value: boolean | undefined) => {
        setCookieConsent(value);
        if (value === undefined) {
            localStorage.removeItem(LOCAL_STORAGE_KEYS.COOKIE_CONSENT);
        } else {
            localStorage.setItem(LOCAL_STORAGE_KEYS.COOKIE_CONSENT, value.toString());
        }
    };

    return { data: cookieConsent, setCookieConsent: setCookieConsentValue };
};
